// Autenticazione centralizzata delle chiamate HTTP verso il backend.
//
// Il backend richiede un token JWT valido su tutte le API (tranne login/verify/health).
// Invece di modificare ogni componente, qui:
//   1. si aggiunge `Authorization: Bearer <token>` a ogni richiesta verso /api della stessa
//      origine, sia via axios (istanza di default) sia via fetch;
//   2. se il server risponde 401 (token assente, scaduto o non valido) si puliscono le
//      credenziali salvate e si ricarica la pagina → compare il login.
// Deve essere importato per primo in index.tsx, prima di App.
import axios from 'axios';

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

const toUrl = (u: string): URL | null => {
  try {
    return new URL(u, window.location.origin);
  } catch {
    return null;
  }
};

const isApiUrl = (u: string): boolean => {
  const url = toUrl(u);
  return !!url && url.origin === window.location.origin && url.pathname.startsWith('/api/');
};

// Le rotte di login/verifica gestiscono da sole il 401 (es. password sbagliata)
const isAuthUrl = (u: string): boolean => {
  const url = toUrl(u);
  return !!url && url.pathname.startsWith('/api/auth/');
};

let redirecting = false;
const handleUnauthorized = () => {
  if (redirecting) return;
  redirecting = true;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  window.location.reload();
};

// ── axios ──
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  const url = `${config.baseURL || ''}${config.url || ''}`;
  if (token && isApiUrl(url) && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = `${error?.config?.baseURL || ''}${error?.config?.url || ''}`;
    if (error?.response?.status === 401 && isApiUrl(url) && !isAuthUrl(url)) {
      handleUnauthorized();
    }
    return Promise.reject(error);
  }
);

// ── fetch ──
const originalFetch = window.fetch.bind(window);

window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  const token = localStorage.getItem(TOKEN_KEY);

  let finalInit = init;
  if (token && isApiUrl(url)) {
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    if (!headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    finalInit = { ...init, headers };
  }

  const response = await originalFetch(input, finalInit);
  if (response.status === 401 && isApiUrl(url) && !isAuthUrl(url)) {
    handleUnauthorized();
  }
  return response;
};

export {};
